// This component handles quota-related banner scenarios surfaced by `useQuotaBanner()`,
// including both free-tier and paid-tier states. Centralizing the rendering and messaging
// here keeps quota-banner behavior consistent as TBB rollout and plan-specific scenarios evolve.
import {sendEvent} from '@github-ui/hydro-analytics'
import {Banner, Link, LinkButton} from '@primer/react'
import {useMemo} from 'react'

import type {QuotaBannerVariant} from '../../hooks/use-quota-banner'
import {useQuotaBannerShownTelemetry} from '../../hooks/use-quota-banner-shown-telemetry'
import {
  ENABLE_ADDITIONAL_REQUESTS_URL,
  INCREASE_BUDGET_URL,
  MANAGE_BUDGET_URL,
  UPGRADE_PLAN_URL_NEW,
} from '../../utils/copilot-chat-helpers'
import {copilotFeatureFlags} from '../../utils/copilot-feature-flags'
import {useChatStateValues} from '../../utils/CopilotChatContext'
import {useEntitlement} from './EntitlementContext'
import styles from './QuotaBanner.module.css'

const BANNER_TARGET_BY_VARIANT: Partial<Record<QuotaBannerVariant, string>> = {
  freeApproaching: 'QUOTA_BANNER_APPROACHING',
  paidApproaching: 'QUOTA_BANNER_APPROACHING',
  overageApproaching: 'QUOTA_BANNER_APPROACHING',
  userLevelBudgetProgress: 'QUOTA_BANNER_APPROACHING',
  freeAlmostOut: 'QUOTA_BANNER_ALMOST_OUT',
  paidAlmostOut: 'QUOTA_BANNER_ALMOST_OUT',
  overageAlmostOut: 'QUOTA_BANNER_ALMOST_OUT',
  userLevelBudgetAlmostReached: 'QUOTA_BANNER_ALMOST_OUT',
  freeExceeded: 'QUOTA_BANNER_EXCEEDED',
  paidExceeded: 'QUOTA_BANNER_EXCEEDED',
  overageExceeded: 'QUOTA_BANNER_EXCEEDED',
  userLevelBudgetReached: 'QUOTA_BANNER_EXCEEDED',
  userLevelBudgetUsageBlocked: 'QUOTA_BANNER_EXCEEDED',
  overageActive: 'QUOTA_BANNER_OVERAGE_ACTIVE',
  licenseProvisioning: 'QUOTA_BANNER_LICENSE_PROVISIONING',
}

export interface QuotaBannerProps {
  /**
   * Whether the banner should render. Typically comes from `useQuotaBanner()`.
   * Accepting it as a prop lets callers combine it with their own conditions.
   */
  show: boolean
  /** Which TBB scenario to display. */
  variant: QuotaBannerVariant | null
  /** Current usage percentage of the relevant quota, for display in approaching-limit scenarios. */
  quotaUsage?: number | null
  /** Reset label for the currently-relevant quota. */
  resetLabel?: string
  /** Whether to render the "Upgrade plan" button. Defaults to `true`. */
  showUpgradeButton?: boolean
  /** Whether to render the "Enable/Edit overage" button. Defaults to `false`. */
  showOverageButton?: boolean
  /**
   * Whether this banner can be dismissed by the user. When `true` a dismiss
   * button is rendered that calls `onDismiss` when clicked. Defaults to `false`.
   */
  isDismissable?: boolean
  /** If provided, the banner renders a dismiss button that calls this. */
  onDismiss?: () => void
}

interface BannerConfigBase {
  title: string
  description: React.ReactNode | string
  bannerVariant: 'critical' | 'info' | 'warning'
  eventTarget?: string
  // Upgrade/overage fields are omitted for variants that have no actions
  // (e.g. user-level budget). Callers passing showUpgradeButton/showOverageButton
  // for those variants get no buttons rendered.
  upgradeLabel?: string
}

type BannerConfig = BannerConfigBase &
  ({overageLabel: string; overageUrl: string} | {overageLabel?: never; overageUrl?: never})

function getConfig({
  variant,
  quotaUsage,
  resetLabel,
}: {
  variant: QuotaBannerVariant
  quotaUsage: number | null | undefined
  resetLabel: string
}): BannerConfig {
  const resetSentence = resetLabel ? ` Limit resets ${resetLabel}.` : ''

  switch (variant) {
    case 'freeApproaching':
      return {
        title: 'Approaching limit',
        description: `You're approaching the limit of your included usage with the free plan.`,
        bannerVariant: 'info',
        eventTarget: BANNER_TARGET_BY_VARIANT.freeApproaching,
        upgradeLabel: 'Upgrade plan',
      }
    case 'freeAlmostOut':
      return {
        title: 'Almost out',
        description: `You're almost out of your included usage with the free plan.${resetSentence}`,
        bannerVariant: 'warning',
        eventTarget: BANNER_TARGET_BY_VARIANT.freeAlmostOut,
        upgradeLabel: 'Upgrade plan',
      }
    case 'freeExceeded':
      return {
        title: 'Limit reached',
        description: `You've run out of your included usage with the free plan.${resetSentence}`,
        bannerVariant: 'critical',
        eventTarget: BANNER_TARGET_BY_VARIANT.freeExceeded,
        upgradeLabel: 'Upgrade plan',
      }
    case 'paidApproaching':
      return {
        title: 'Approaching limit',
        description: `You've used ${Math.floor(quotaUsage ?? 75)}% of your included AI credits.${resetSentence}`,
        bannerVariant: 'info',
        eventTarget: BANNER_TARGET_BY_VARIANT.paidApproaching,
        upgradeLabel: 'Upgrade plan',
        overageLabel: 'Enable additional usage',
        overageUrl: ENABLE_ADDITIONAL_REQUESTS_URL,
      }
    case 'paidAlmostOut':
      return {
        title: 'Almost out',
        description: `You've used ${Math.floor(quotaUsage ?? 90)}% of your included AI credits.${resetSentence}`,
        bannerVariant: 'warning',
        eventTarget: BANNER_TARGET_BY_VARIANT.paidAlmostOut,
        upgradeLabel: 'Upgrade plan',
        overageLabel: 'Enable additional usage',
        overageUrl: ENABLE_ADDITIONAL_REQUESTS_URL,
      }
    case 'paidExceeded':
      return {
        title: 'Limit reached',
        description: `You've run out of your included AI credits for the month.${resetSentence}`,
        bannerVariant: 'critical',
        eventTarget: BANNER_TARGET_BY_VARIANT.paidExceeded,
        upgradeLabel: 'Upgrade plan',
        overageLabel: 'Enable additional usage',
        overageUrl: ENABLE_ADDITIONAL_REQUESTS_URL,
      }
    case 'overageApproaching':
      return {
        title: 'Approaching budget limit',
        description: `You've used ${Math.floor(quotaUsage ?? 75)}% of your budget for additional AI credits.`,
        bannerVariant: 'info',
        eventTarget: BANNER_TARGET_BY_VARIANT.overageApproaching,
        upgradeLabel: 'Upgrade plan',
        overageLabel: 'Increase budget',
        overageUrl: INCREASE_BUDGET_URL,
      }
    case 'overageAlmostOut':
      return {
        title: 'Almost out of budget',
        description: `You've used ${Math.floor(quotaUsage ?? 90)}% of your budget for additional AI credits.`,
        bannerVariant: 'warning',
        eventTarget: BANNER_TARGET_BY_VARIANT.overageAlmostOut,
        upgradeLabel: 'Upgrade plan',
        overageLabel: 'Increase budget',
        overageUrl: INCREASE_BUDGET_URL,
      }
    case 'overageExceeded':
      return {
        title: 'Budget reached',
        description: `You've run out of your budget for additional AI credits.`,
        bannerVariant: 'critical',
        eventTarget: BANNER_TARGET_BY_VARIANT.overageExceeded,
        upgradeLabel: 'Upgrade plan',
        overageLabel: 'Increase budget',
        overageUrl: INCREASE_BUDGET_URL,
      }
    case 'overageActive':
      return {
        title: 'Additional usage active',
        description: `You're now using additional usage until your included credits reset${
          resetLabel ? ` ${resetLabel}` : ''
        }.`,
        bannerVariant: 'info',
        eventTarget: BANNER_TARGET_BY_VARIANT.overageActive,
        upgradeLabel: 'Upgrade plan',
        overageLabel: 'Manage overage',
        overageUrl: MANAGE_BUDGET_URL,
      }
    case 'userLevelBudgetProgress':
      return {
        title: 'Approaching limit',
        description: `You have used ${Math.floor(quotaUsage ?? 75)}% of your monthly usage limit.`,
        bannerVariant: 'info',
        eventTarget: BANNER_TARGET_BY_VARIANT.userLevelBudgetProgress,
      }
    case 'userLevelBudgetAlmostReached':
      return {
        title: 'Almost at limit',
        description: `You have used ${Math.floor(quotaUsage ?? 75)}% of your monthly usage limit.`,
        bannerVariant: 'warning',
        eventTarget: BANNER_TARGET_BY_VARIANT.userLevelBudgetAlmostReached,
      }
    case 'userLevelBudgetReached':
      return {
        title: 'Usage paused',
        description: `Usage paused until your monthly limit resets ${resetLabel}. Request your administrator for additional usage.`,
        bannerVariant: 'critical',
        eventTarget: BANNER_TARGET_BY_VARIANT.userLevelBudgetReached,
      }
    case 'userLevelBudgetUsageBlocked':
      return {
        title: 'Usage blocked',
        description: 'Usage is currently blocked by your organization. Contact your administrator to resume usage.',
        bannerVariant: 'critical',
        eventTarget: BANNER_TARGET_BY_VARIANT.userLevelBudgetUsageBlocked,
      }
    case 'licenseProvisioning':
      return {
        title: 'Setting up your Copilot license',
        description: 'Cleared for takeoff soon—your Copilot license is on its way. Please check back in a few minutes.',
        bannerVariant: 'info',
        eventTarget: BANNER_TARGET_BY_VARIANT.licenseProvisioning,
      }
  }
}

/**
 * Unified banner for all TBB (token-based billing) quota scenarios.
 *
 * Pair with `useQuotaBanner()` which computes `show`, `variant`,
 * and which action buttons to render:
 *
 * ```tsx
 * const quotaBanner = useQuotaBanner()
 * return <QuotaBanner {...quotaBanner} />
 * ```
 */
export function QuotaBanner({
  show,
  variant,
  quotaUsage = null,
  resetLabel = '',
  showUpgradeButton = true,
  showOverageButton = false,
  isDismissable = false,
  onDismiss,
}: QuotaBannerProps) {
  const {plan, tokenBasedBillingEnabled} = useEntitlement()
  const {mode} = useChatStateValues('mode')
  const eventVariant = useMemo(() => {
    if (!show || !variant) return null
    return variant
  }, [show, variant])

  useQuotaBannerShownTelemetry({
    target: eventVariant ? BANNER_TARGET_BY_VARIANT[eventVariant] : undefined,
    mode,
    plan,
    tokenBasedBillingEnabled,
    enabled: !!eventVariant,
    dedupeKey: eventVariant ?? undefined,
    variant: eventVariant,
    quotaUsage,
  })

  if (!show || !variant) return null

  const config = getConfig({variant, quotaUsage, resetLabel})

  const description = <>{config.description}</>

  // A variant may opt out of having an upgrade or overage CTA by omitting the
  // corresponding label in getConfig() (e.g. user-level budget banners). When
  // the label is missing, we never render the button even if the caller passed
  // the show flag.
  const canShowUpgrade = showUpgradeButton && !!config.upgradeLabel
  const canShowOverage = showOverageButton && !!config.overageLabel

  const sendQuotaBannerEvent = (action: 'upgrade' | 'overages' | 'dismiss') => {
    if (!config.eventTarget) return

    sendEvent('dotcom_chat.activate', {
      target: config.eventTarget,
      action,
      mode,
      tokenBasedBillingEnabled,
      plan,
      quotaUsage,
      variant,
    })
  }

  const upgradeButton = canShowUpgrade ? (
    <LinkButton
      as="a"
      href={UPGRADE_PLAN_URL_NEW}
      onClick={() => {
        sendQuotaBannerEvent('upgrade')
      }}
    >
      {config.upgradeLabel}
    </LinkButton>
  ) : null

  const overageButton = canShowOverage ? (
    <LinkButton
      as="a"
      href={copilotFeatureFlags.ubbNewCtaDestinations ? config.overageUrl : MANAGE_BUDGET_URL}
      onClick={() => {
        sendQuotaBannerEvent('overages')
      }}
    >
      {config.overageLabel}
    </LinkButton>
  ) : null

  // When both actions are shown, keep the upgrade path as primary (link-styled
  // to de-emphasize) and the overage button as the strong CTA. When only one
  // is shown, it becomes the primary action.
  let primaryAction: React.ReactNode = null
  if (canShowUpgrade && canShowOverage) {
    primaryAction = (
      <>
        <Link
          as="a"
          href={UPGRADE_PLAN_URL_NEW}
          onClick={() => {
            sendQuotaBannerEvent('upgrade')
          }}
        >
          {config.upgradeLabel}
        </Link>
        {overageButton}
      </>
    )
  } else {
    primaryAction = upgradeButton ?? overageButton
  }

  const handleOnDismiss = () => {
    onDismiss?.()
    if (onDismiss) {
      sendQuotaBannerEvent('dismiss')
    }
  }

  return (
    <div className={styles.container}>
      <Banner
        variant={config.bannerVariant}
        title={config.title}
        description={description}
        hideTitle
        primaryAction={primaryAction}
        onDismiss={isDismissable ? handleOnDismiss : undefined}
      />
    </div>
  )
}
